import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, CheckCircle, AlertCircle, Download, Shield, ShieldCheck, Lock, Hash } from 'lucide-react';
import { Card, CardContent } from '../components/new-components/ui/card';
import { Button } from '../components/new-components/ui/button';
import { Input } from '../components/new-components/ui/input';
import { SignaturePad } from '../components/new-components/SignaturePad';
import { TENANT } from '../config';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

/**
 * Whose name is on this page.
 *
 * This used to be `TENANT_ID === 'redex' ? 'REDEX' : 'Honeyshares'` — a
 * two-way choice in a codebase that has four tenants, so every tenant that
 * was not REDEX was branded Honeyshares. eMerge employees opening a company
 * policy were shown another company's name, on the one page in the platform
 * that a person outside the company can be sent a link to.
 *
 * The tenant registry already carries the right answer. Reading it means a
 * fifth tenant is branded correctly by existing, rather than by somebody
 * remembering to extend a ternary.
 */
const PLATFORM_NAME = TENANT.branding.name;

const TIER_LABELS: Record<number, { label: string; color: string }> = {
  1: { label: 'Standard', color: 'text-blue-600 bg-blue-50' },
  2: { label: 'Commercial', color: 'text-amber-600 bg-amber-50' },
  3: { label: 'High Security', color: 'text-red-600 bg-red-50' },
};

const SignDocument: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [sigData, setSigData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [agreed, setAgreed] = useState(false);

  // OTP state
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState('');

  // Signature image (visual drawn signature)
  const [signatureImage, setSignatureImage] = useState('');

  // Completion data
  const [signResult, setSignResult] = useState<any>(null);

  // The name is what the signer typed, trimmed. Spaces are not a name: the
  // server refuses a blank typed signature (NO_MARK), so the page never offers
  // one, never previews a blank mark, and never sends the padding.
  const typedName = signerName.trim();
  const namesMatch = typedName.length > 0 && typedName === confirmName.trim();

  const loadSignature = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/esign/${id}`);
      const data = await res.json();
      if (data.success) {
        setSigData(data.data);
        if (data.data.signerName) setSignerName(data.data.signerName);
        if (data.data.signerEmail) setSignerEmail(data.data.signerEmail);
        if (data.data.status === 'signed') setSigned(true);
        if (data.data.requiresOtp) setOtpRequired(true);
        if (data.data.otpVerified) setOtpVerified(true);
      } else {
        setError(data.message || 'Signature request not found');
      }
    } catch {
      setError('Failed to load signature request');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadSignature(); }, [loadSignature]);

  const handleRequestOtp = async () => {
    setOtpSending(true);
    setOtpError('');
    try {
      const res = await fetch(`${API_BASE}/esign/${id}/request-otp`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setOtpSent(true);
      } else {
        setOtpError(data.message || 'Failed to send verification code');
      }
    } catch {
      setOtpError('Failed to send verification code');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    setOtpVerifying(true);
    setOtpError('');
    try {
      const res = await fetch(`${API_BASE}/esign/${id}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otp: otpCode }),
      });
      const data = await res.json();
      if (data.success) {
        setOtpVerified(true);
        setOtpRequired(false);
      } else {
        setOtpError(data.message || 'Invalid code');
      }
    } catch {
      setOtpError('Verification failed');
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleSign = async () => {
    if (!agreed || !namesMatch) return;
    setSigning(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/esign/${id}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signerName: typedName,
          signerEmail: signerEmail || undefined,
          signatureImage: signatureImage || undefined,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSigned(true);
        setSignResult(data.data);
      } else if (data.requiresOtp) {
        setOtpRequired(true);
        setError('');
      } else {
        setError(data.message || 'Failed to sign document');
      }
    } catch {
      setError('Failed to submit signature');
    } finally {
      setSigning(false);
    }
  };

  const fmtBytes = (b?: number) => {
    if (!b) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }

  if (error && !sigData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Error</h2>
            <p className="text-sm text-gray-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Document Signed</h2>
            <p className="text-sm text-gray-500 mb-4">
              You have successfully signed "{sigData?.documentTitle}". A signed copy has been created for your records.
            </p>
            <p className="text-xs text-gray-400 mb-4">
              Signed by {signerName} on {new Date().toLocaleDateString()}
            </p>
            {signResult?.documentHash && (
              <div className="mt-4 border rounded-lg p-3 bg-gray-50 text-left">
                <p className="text-xs font-medium text-gray-600 flex items-center gap-1">
                  <Hash className="h-3 w-3" /> Document Integrity Hash (SHA-256)
                </p>
                <p className="text-xs font-mono text-gray-400 mt-1 break-all">{signResult.documentHash}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const riskTier = sigData?.riskTier || 1;
  const tierInfo = TIER_LABELS[riskTier] || TIER_LABELS[1];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">{PLATFORM_NAME}</h1>
          <p className="text-sm text-gray-500 mt-1">Document Signature Request</p>
        </div>

        {/* Document Info */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <FileText className="h-8 w-8 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900">{sigData?.documentTitle}</h2>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tierInfo.color}`}>
                    {tierInfo.label}
                  </span>
                </div>
                {sigData?.dealTitle && (
                  <p className="text-sm text-gray-500 mt-1">Related to: {sigData.dealTitle}</p>
                )}
                <div className="flex gap-4 mt-2 text-xs text-gray-400">
                  {sigData?.fileName && <span>{sigData.fileName}</span>}
                  {sigData?.fileSize && <span>{fmtBytes(Number(sigData.fileSize))}</span>}
                </div>
                {sigData?.downloadUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => window.open(`${API_BASE}/esign/${id}/document`, '_blank')}
                  >
                    <Download className="h-3 w-3 mr-1" /> View Document
                  </Button>
                )}
                {sigData?.documentHash && (
                  <div className="mt-3 flex items-center gap-1 text-xs text-gray-400">
                    <Shield className="h-3 w-3" />
                    <span className="font-mono truncate max-w-[300px]">SHA-256: {sigData.documentHash}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* OTP Verification (Tier 3) */}
        {otpRequired && !otpVerified && (
          <Card className="border-amber-200 bg-amber-50/30">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-amber-600" />
                <h3 className="font-semibold text-gray-900">Identity Verification Required</h3>
              </div>
              <p className="text-sm text-gray-600">
                This document requires enhanced verification. A one-time code will be sent to your email address on file.
              </p>

              {!otpSent ? (
                <Button onClick={handleRequestOtp} disabled={otpSending} className="w-full">
                  {otpSending ? 'Sending...' : 'Send Verification Code'}
                </Button>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-green-600 font-medium">
                    Code sent to your email. Check your inbox.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={otpCode}
                      onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit code"
                      className="text-center text-lg tracking-widest font-mono"
                      maxLength={6}
                    />
                    <Button onClick={handleVerifyOtp} disabled={otpVerifying || otpCode.length !== 6}>
                      {otpVerifying ? 'Verifying...' : 'Verify'}
                    </Button>
                  </div>
                  <button
                    onClick={handleRequestOtp}
                    disabled={otpSending}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Resend code
                  </button>
                </div>
              )}
              {otpError && <p className="text-sm text-red-600">{otpError}</p>}
            </CardContent>
          </Card>
        )}

        {/* OTP Verified Badge */}
        {otpVerified && riskTier >= 3 && (
          <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
            <ShieldCheck className="h-4 w-4" />
            Identity verified — you may proceed to sign.
          </div>
        )}

        {/* Signing Form */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <h3 className="font-semibold text-gray-900">Sign This Document</h3>

            <div>
              <label className="text-sm text-gray-600 mb-1 block">Type Your Full Legal Name *</label>
              <Input
                value={signerName}
                onChange={e => setSignerName(e.target.value)}
                placeholder="e.g. John Michael Smith"
                className="text-lg font-medium"
                disabled={otpRequired && !otpVerified}
              />
              <p className="text-xs text-gray-400 mt-1">Type your full legal name exactly as it appears on your government-issued ID.</p>
            </div>

            <div>
              <label className="text-sm text-gray-600 mb-1 block">Confirm Full Legal Name *</label>
              <Input
                value={confirmName}
                onChange={e => setConfirmName(e.target.value)}
                placeholder="Re-type your full legal name"
                className="text-lg font-medium"
                disabled={otpRequired && !otpVerified}
              />
              {confirmName.trim() && typedName && confirmName.trim() !== typedName && (
                <p className="text-xs text-red-500 mt-1">Names do not match.</p>
              )}
            </div>

            <div>
              <label className="text-sm text-gray-600 mb-1 block">Email Address</label>
              <Input
                type="email"
                value={signerEmail}
                onChange={e => setSignerEmail(e.target.value)}
                placeholder="your@email.com"
                disabled={otpRequired && !otpVerified}
              />
            </div>

            {/* Visual Signature Pad */}
            {namesMatch && (
              <SignaturePad
                onSignatureCapture={setSignatureImage}
                label="Draw Your Signature"
                disabled={otpRequired && !otpVerified}
                existingSignature={signatureImage || undefined}
              />
            )}

            {/* Typed fallback preview (when no drawn signature) */}
            {namesMatch && !signatureImage && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <p className="text-xs text-gray-400 mb-2">Typed Signature (or draw above)</p>
                <p className="text-2xl font-serif italic text-gray-800">{typedName}</p>
                <div className="border-b border-gray-400 mt-1" />
              </div>
            )}

            <div className="flex items-start gap-2 pt-2">
              <input
                type="checkbox"
                id="agree"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                className="mt-1"
                disabled={otpRequired && !otpVerified}
              />
              <label htmlFor="agree" className="text-sm text-gray-600">
                I have reviewed this document and agree to sign it electronically. I understand this constitutes a legally binding signature under the ESIGN Act and UETA.
              </label>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <Button
              className="w-full"
              onClick={handleSign}
              disabled={!namesMatch || !agreed || signing || (otpRequired && !otpVerified)}
            >
              {signing ? 'Signing...' : 'I Agree & Sign Document'}
            </Button>

            <div className="flex items-center justify-center gap-1 text-xs text-gray-400">
              <Shield className="h-3 w-3" />
              Your IP address and browser information will be recorded for audit purposes.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SignDocument;
